import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { archiveTask } from "../../actions/userActions/archiveTaskAction";
import Loader from "../subComponents/Loader";
import moment from "moment";
import { Tooltip } from "@mui/material";
import {
  getSubOrdinates,
  getTasks,
  getTaskStatuses,
  getTaskTypes,
} from "../../actions/userActions/tasks/taskAction";
import { getTaskPriorities } from "../../actions/userActions/taskPriorityAction";
import { SIZE_OF_PAGE } from "../../actions/types";
import SearchInput from "../subComponents/search/SearchInput";
import CustomButton from "../subComponents/buttons/CustomButton";
import ItemsPerPageSelector from "../subComponents/pagination/ItemsPerPageSelector";
import CustomPagination from "../subComponents/pagination/CustomPagination";
import CustomFilterSelect from "../designSystem/CustomFilterSelect";
import GenericFilter, { FilterBar } from "../subComponents/Filter/GenericFilters";
import DynamicTable from "../reuse_components/DynamicTable";
import LoadingOverlay from "../loadingOverlay/LoadingOverlay";
import { t } from "../../locales/translate";
import { PERMISSIONS } from "../../Permissions";
import {
  formatTaskDistance,
  formatTaskFuel,
} from "../../utils/travelFormat";

const CompletedTask = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter states
  const [dateRangeFilter, setDateRangeFilter] = useState('last 7 days');
  const [taskTypeFilter, setTaskTypeFilter] = useState('');
  const [taskPriorityFilter, setTaskPriorityFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [textSearchFilter, setTextSearchFilter] = useState('');

  // Date state
  const [fromDate, setFromDate] = useState(moment().subtract(7, "days").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));

  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem("user"));
  const permissions = JSON.parse(localStorage.getItem("permissionsList"));

  // Redux selectors
  const ArchiveTasks = useSelector((state) => state.archive_task_reducer.archiveTask);
  const taskTypes = useSelector((state) => state.tasks_reducer.taskTypes);
  const taskPriorities = useSelector((state) => state.tasks_reducer.taskPriorities);
  const taskPrioritiesData = taskPriorities && taskPriorities.result;
  const employees = useSelector((state) => state.tasks_reducer.subOrdinates);
  const isTaskTypeLoading = useSelector((state) => state.tasks_reducer.isTaskTypeLoading);
  const isEmployeesLoading = useSelector((state) => state.employees_reducer.isEmployeesLoading);
  const isTaskPriorityLoading = useSelector((state) => state.tasks_reducer.isTaskPriorityLoading);
  const isArchiveTaskLoading = useSelector((state) => state.archive_task_reducer.isArchiveTaskLoading);
  const isLoading = isArchiveTaskLoading || isEmployeesLoading || isTaskPriorityLoading || isTaskTypeLoading;

  const totalPages = ArchiveTasks?.totalPages || 0;
  const totalItems = ArchiveTasks?.totalCount || 0;
  const employeeId = user.result[0] !== undefined ? user.result[0].user.id : false;

  // Initialize filters with default values
  const getDefaultFilters = () => ({
    taskType: taskTypeFilter ? [taskTypeFilter] : [],
    taskPriority: taskPriorityFilter ? [taskPriorityFilter] : [],
    taskStatus: [],
    taskAssignee: employeeFilter ? [employeeFilter] : [],
    textSearch: textSearchFilter,
    taskFromDate: fromDate,
    taskToDate: toDate,
  });

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleClearAllFilters = () => {
    setDateRangeFilter('last 7 days');
    setTaskTypeFilter('');
    setTaskPriorityFilter('');
    setEmployeeFilter('');
    setTextSearchFilter('');
    setCurrentPage(1);

    // Reset to last 7 days
    const newFromDate = moment().subtract(7, "days").format("YYYY-MM-DD");
    const newToDate = moment().format("YYYY-MM-DD");
    setFromDate(newFromDate);
    setToDate(newToDate);

    // Dispatch with cleared filters
    const body = {
      filter: {
        taskType: [],
        taskPriority: [],
        taskStatus: [],
        taskAssignee: [],
        textSearch: '',
        taskFromDate: newFromDate,
        taskToDate: newToDate,
      },
      pageNumber: 1,
      pageSize: itemsPerPage,
      employeeId: user.result[0].user.id,
    };
    dispatch(archiveTask(body));
  };

  const handleDateRangeChange = (event) => {
    const value = event.target.value;
    setDateRangeFilter(value);
    setCurrentPage(1);

    let newFromDate, newToDate;

    switch (value) {
      case 'today':
        newFromDate = moment().format("YYYY-MM-DD");
        newToDate = moment().format("YYYY-MM-DD");
        break;
      case 'yesterday':
        newFromDate = moment().subtract(1, "days").format("YYYY-MM-DD");
        newToDate = moment().subtract(1, "days").format("YYYY-MM-DD");
        break;
      case 'last 7 days':
        newFromDate = moment().subtract(7, "days").format("YYYY-MM-DD");
        newToDate = moment().format("YYYY-MM-DD");
        break;
      case 'last 30 days':
        newFromDate = moment().subtract(30, "days").format("YYYY-MM-DD");
        newToDate = moment().format("YYYY-MM-DD");
        break;
      case 'this month':
        newFromDate = moment().startOf("month").format("YYYY-MM-DD");
        newToDate = moment().endOf("month").format("YYYY-MM-DD");
        break;
      case 'last month':
        newFromDate = moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
        newToDate = moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD");
        break;
      case 'this year':
        newFromDate = moment().startOf("year").format("YYYY-MM-DD");
        newToDate = moment().endOf("year").format("YYYY-MM-DD");
        break;
      case 'last year':
        newFromDate = moment().subtract(1, "year").startOf("year").format("YYYY-MM-DD");
        newToDate = moment().subtract(1, "year").endOf("year").format("YYYY-MM-DD");
        break;
      default:
        newFromDate = moment().subtract(7, "days").format("YYYY-MM-DD");
        newToDate = moment().format("YYYY-MM-DD");
    }

    setFromDate(newFromDate);
    setToDate(newToDate);

    // Dispatch with new date range
    const body = {
      filter: {
        ...getDefaultFilters(),
        taskFromDate: newFromDate,
        taskToDate: newToDate,
      },
      pageNumber: currentPage,
      pageSize: itemsPerPage,
      employeeId: user.result[0].user.id,
    };
    dispatch(archiveTask(body));
  };

  const handleTaskTypeChange = (event) => {
    const value = event.target.value;
    setTaskTypeFilter(value);
    setCurrentPage(1);

    const body = {
      filter: {
        ...getDefaultFilters(),
        taskType: value ? [value] : [],
      },
      pageNumber: 1,
      pageSize: itemsPerPage,
      employeeId: user.result[0].user.id,
    };
    dispatch(archiveTask(body));
  };

  const handleTaskPriorityChange = (event) => {
    const value = event.target.value;
    setTaskPriorityFilter(value);
    setCurrentPage(1);

    const body = {
      filter: {
        ...getDefaultFilters(),
        taskPriority: value ? [value] : [],
      },
      pageNumber: 1,
      pageSize: itemsPerPage,
      employeeId: user.result[0].user.id,
    };
    dispatch(archiveTask(body));
  };

  const handleEmployeeChange = (event) => {
    const value = event.target.value;
    setEmployeeFilter(value);
    setCurrentPage(1);

    const body = {
      filter: {
        ...getDefaultFilters(),
        taskAssignee: value ? [value] : [],
      },
      pageNumber: 1,
      pageSize: itemsPerPage,
      employeeId: user.result[0].user.id,
    };
    dispatch(archiveTask(body));
  };

  const handleSearchChange = (query) => {
    setTextSearchFilter(query);
    setCurrentPage(1);

    const body = {
      filter: {
        ...getDefaultFilters(),
        textSearch: query,
      },
      pageNumber: 1,
      pageSize: itemsPerPage,
      employeeId: user.result[0].user.id,
    };
    dispatch(archiveTask(body));
  };

  const handleItemsPerPageChange = (value) => {
    const body = {
      filter: getDefaultFilters(),
      pageNumber: 1,
      pageSize: value,
      employeeId: user.result[0].user.id,
    };
    dispatch(archiveTask(body));
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const body = {
      filter: getDefaultFilters(),
      pageNumber: page,
      pageSize: itemsPerPage,
      employeeId: user.result[0].user.id,
    };
    dispatch(archiveTask(body));
  };

  // Initial data fetch
  useEffect(() => {
    const pageNumber = -1;
    const body = {
      filter: getDefaultFilters(),
      pageNumber: currentPage,
      pageSize: itemsPerPage,
      employeeId: user.result[0].user.id,
    };

    dispatch(archiveTask(body))
      .then(() => {
        dispatch(getTasks());
        dispatch(getTaskTypes());
        dispatch(getTaskPriorities(pageNumber, SIZE_OF_PAGE, searchQuery));
        dispatch(getTaskStatuses());
        dispatch(getSubOrdinates(employeeId, -1, 0, ""));
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  }, []);

  // Define table columns
  const taskColumns = [
    { label: `${t("title")}`, accessor: "title" },
    { label: `${t("assignedTo")}`, accessor: "assigneeName" },
    { label: `${t("status")}`, accessor: "taskStatusName" },
    { label: `${t("taskPriority")}`, accessor: "taskPriorityName" },
    { label:`${t("date")}`, accessor: "taskCreateDate" },
    ...(permissions?.includes(PERMISSIONS.CAN_VIEW_EMPLOYEE_TASK_STATS)
      ? [
          { label: t("distance"), accessor: "distance" },
          { label: t("fuel"), accessor: "totalCost" },
        ]
      : []),
  ];

  // Transform data for the table
  const transformedTasks =
    ArchiveTasks !== isArchiveTaskLoading &&
    ArchiveTasks?.result &&
    ArchiveTasks?.result &&
    ArchiveTasks?.result?.length > 0 &&
    ArchiveTasks.result.map((task) => ({
      ...task,
      taskCreateDate: moment(task.taskCreateDate).format("ddd, MMM D, YYYY"),
      taskStatusName: (
        <span
          className="badge badge-lg badge-dot"
          style={{ color: task.taskStatusColor }}
        >
          <i className="bg-success" />
          {task.taskStatusName}
        </span>
      ),
      taskPriorityName: (
        <span style={{ color: task.taskPriorityColor }}>
          {task.taskPriorityName}
        </span>
      ),
      ...(permissions?.includes(PERMISSIONS.CAN_VIEW_EMPLOYEE_TASK_STATS)
        ? {
            distance: formatTaskDistance(task.distance),
            totalCost: formatTaskFuel(task.totalCost),
          }
        : {}),
    }));

  return (
    <>
      <div>
        <LoadingOverlay isVisible={isLoading} />
        <div className="main-container">
          <div className="content-wrapper p-0">
              <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '20px 16px'
            }}>
              <div className="task-header-left">
                <SearchInput
                  placeholder="Search Task"
                  onChange={(e) => handleSearchChange(e.target.value)}
                  value={textSearchFilter}
                />
              </div>

              <div className="task-header-right" style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px' 
              }}>
                <div style={{ minWidth: '80px' }}>
                  <ItemsPerPageSelector
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                  />
                </div>

                <CustomPagination
                  currentPage={currentPage}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />

                <GenericFilter showFilters={showFilters} toggleFilters={toggleFilters} />
              </div>
            </div>

            {/* Filter Bar using Generic Filter Component */}
            <FilterBar showFilters={showFilters} onClearAll={handleClearAllFilters}>
              {/* Date Range Filter */}
              <CustomFilterSelect
                label="Date Range"
                width="160px"
                value={dateRangeFilter}
                searchable={false}
                onChange={handleDateRangeChange}
                options={[
                  { id: 'today', name: 'Today' },
                  { id: 'yesterday', name: 'Yesterday' },
                  { id: 'last 7 days', name: 'Last 7 Days' },
                  { id: 'last 30 days', name: 'Last 30 Days' },
                  { id: 'this month', name: 'This Month' },
                  { id: 'last month', name: 'Last Month' },
                  { id: 'this year', name: 'This Year' },
                  { id: 'last year', name: 'Last Year' },
                ]}
              />

              {/* Task Type Filter */}
              {/* <CustomFilterSelect
                label={t("type")}
                width="180px"
                value={taskTypeFilter}
                searchPlaceholder="Search type"
                onChange={handleTaskTypeChange}
                options={[
                  { id: '', name: 'All Types' },
                  ...(Array.isArray(taskTypes)
                    ? taskTypes
                        .filter((taskType) => taskType.active)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(type => ({
                          id: type.id,
                          name: type.name
                        }))
                    : [])
                ]}
              /> */}

              {/* Task Priority Filter */}
              <CustomFilterSelect
                label={t("priority")}
                width="180px"
                value={taskPriorityFilter}
                searchPlaceholder="Search priority"
                onChange={handleTaskPriorityChange}
                options={[
                  { id: '', name: 'All Priorities' },
                  ...(Array.isArray(taskPrioritiesData)
                    ? taskPrioritiesData
                        .filter((priority) => priority.active)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(priority => ({
                          id: priority.id,
                          name: priority.name
                        }))
                    : [])
                ]}
              />

              {/* Employee Filter */}
              <CustomFilterSelect
                label={t("employee")}
                width="200px"
                value={employeeFilter}
                searchPlaceholder="Search employee"
                onChange={handleEmployeeChange}
                options={[
                  { id: '', name: 'All Employees' },
                  ...(Array.isArray(employees)
                    ? employees
                        .sort((a, b) => a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName))
                        .map(employee => ({
                          id: employee.id,
                          name: `${employee.firstName} ${employee.lastName}`
                        }))
                    : [])
                ]}
              />
            </FilterBar>
          </div>

          {/* Dynamic Table */}
          <DynamicTable
            data={transformedTasks || []}
            columns={taskColumns}
            showPagination={totalItems > 0}
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      </div>
    </>
  );
};

export default CompletedTask;